import { AlertTriangle, Banknote, Scale, Zap, Clock } from 'lucide-react';
import { MetricCard } from '@/components/MetricCard';
import { formatCurrency } from '@/lib/utils';
import type { AccDashboardKpis, HealthState } from '../types';
import { cn } from '@/lib/utils';

interface KpiRowProps {
  kpis: AccDashboardKpis;
  degraded: string[];
  loading: boolean;
  /** Scroll-to anchors: call these when the matching KPI tile is clicked */
  onScrollToReviewQueue: () => void;
  onScrollToCashQueue: () => void;
}

const HEALTH_CLASSES: Record<HealthState, string> = {
  green: 'bg-green-100 text-green-800 border-green-200',
  amber: 'bg-amber-100 text-amber-800 border-amber-200',
  red: 'bg-red-100 text-red-800 border-red-200',
};

export function HealthBadge({ state, reason }: { state: HealthState; reason: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold',
        HEALTH_CLASSES[state],
      )}
      title={reason}
    >
      <span
        className={cn(
          'h-1.5 w-1.5 rounded-full',
          state === 'green' ? 'bg-green-600' : state === 'amber' ? 'bg-amber-600' : 'bg-red-600',
        )}
        aria-hidden
      />
      {state.charAt(0).toUpperCase() + state.slice(1)}
    </span>
  );
}

function UnavailableTile({ title }: { title: string }) {
  return (
    <div className="flex flex-col gap-1 rounded-lg border border-dashed border-border p-4">
      <p className="text-sm text-muted-foreground">{title}</p>
      <p className="text-xs text-muted-foreground/60">Unavailable</p>
    </div>
  );
}

export function KpiRow({
  kpis,
  degraded,
  loading,
  onScrollToReviewQueue,
  onScrollToCashQueue,
}: KpiRowProps) {
  const isNeedsReviewDegraded = degraded.includes('needsReview');
  const isCashToApplyDegraded = degraded.includes('cashToApply');
  const isUnreconciledDegraded = degraded.includes('unreconciled');
  const isStrDegraded = degraded.includes('straightThroughRate');
  const isAgeDegraded = degraded.includes('avgOpenDays');

  const strPct =
    kpis.straightThroughRate !== null
      ? `${(kpis.straightThroughRate * 100).toFixed(1)}%`
      : null;

  const cashLabel =
    kpis.cashToApply !== null
      ? `${kpis.cashToApply.count} items · ${formatCurrency(kpis.cashToApply.amount)}`
      : null;

  const unreconciledLabel =
    kpis.unreconciled !== null ? formatCurrency(kpis.unreconciled.amount) : null;

  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
      {/* Needs review */}
      {isNeedsReviewDegraded ? (
        <UnavailableTile title="Needs review" />
      ) : (
        <button
          type="button"
          className="text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded-lg"
          onClick={onScrollToReviewQueue}
          aria-label="Scroll to review queue"
        >
          <MetricCard
            title="Needs review"
            value={kpis.needsReview?.count ?? null}
            icon={<AlertTriangle className="h-5 w-5" />}
            color="red"
            loading={loading}
            tooltip="Open acc_exceptions — items the engine flagged and could not auto-resolve."
            className="cursor-pointer hover:shadow-md transition-shadow"
          />
        </button>
      )}

      {/* Cash to apply */}
      {isCashToApplyDegraded ? (
        <UnavailableTile title="Cash to apply" />
      ) : (
        <button
          type="button"
          className="text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded-lg"
          onClick={onScrollToCashQueue}
          aria-label="Scroll to cash-to-apply list"
        >
          <MetricCard
            title="Cash to apply"
            value={cashLabel}
            icon={<Banknote className="h-5 w-5" />}
            color="orange"
            loading={loading}
            tooltip="Matched bank deposits whose payment has not yet been applied to an invoice."
            className="cursor-pointer hover:shadow-md transition-shadow"
          />
        </button>
      )}

      {/* Unreconciled */}
      {isUnreconciledDegraded ? (
        <UnavailableTile title="Unreconciled" />
      ) : (
        <MetricCard
          title="Unreconciled"
          value={unreconciledLabel}
          icon={<Scale className="h-5 w-5" />}
          color="purple"
          loading={loading}
          tooltip="Sum of acc_statement_reconciliation rows that have not been tied out."
          suffix={kpis.unreconciled ? `(${kpis.unreconciled.count})` : undefined}
        />
      )}

      {/* Straight-through rate */}
      {isStrDegraded ? (
        <UnavailableTile title="Straight-through %" />
      ) : (
        <MetricCard
          title="Straight-through %"
          value={strPct}
          icon={<Zap className="h-5 w-5" />}
          color="green"
          loading={loading}
          tooltip="Percentage of transactions the engine processed without any exception. Higher is better."
        />
      )}

      {/* Oldest open */}
      {isAgeDegraded ? (
        <UnavailableTile title="Oldest open" />
      ) : (
        <MetricCard
          title="Oldest open"
          value={kpis.avgOpenDays !== null ? kpis.avgOpenDays : null}
          icon={<Clock className="h-5 w-5" />}
          color="blue"
          suffix={kpis.avgOpenDays !== null ? 'd avg' : undefined}
          loading={loading}
          tooltip="Average age in days of open exceptions and unapplied deposits. Anything above 30 is worth investigating."
        />
      )}
    </div>
  );
}
